import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Plus, XCircle, AlertTriangle, Mail, Phone } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { Modal } from '../../components/ui/Modal.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Textarea } from '../../components/ui/Form.jsx';

// =============================================================================
// Job offers — dispatch side
// =============================================================================
//   JobOfferSection — sits in the Allocation card. Shows "Send offer" button
//                     for confirmed + unallocated bookings, or the live offer
//                     status (sent to N, opened by M) with Edit/Withdraw.
//   SendOfferModal  — pick classes → pick recipients → set pay → send.
//
// Backend: routes/offers.js
// =============================================================================

const CLASS_OPTIONS = [
    { key: 'business', label: 'Business' },
    { key: 'first',    label: 'First' },
    { key: 'suv',      label: 'SUV' },
    { key: 'xl',       label: 'XL' },
];

function money(n) {
    return `£${(Number(n) || 0).toFixed(2)}`;
}

// -----------------------------------------------------------------------------
// JobOfferSection
// -----------------------------------------------------------------------------
export function JobOfferSection({ booking }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);

    const canOffer = booking.status === 'confirmed' && !booking.allocated_chauffeur_id;

    const offerQ = useQuery({
        queryKey: ['booking', booking.id, 'offer'],
        queryFn: () => api.get(`/offers/booking/${booking.id}`),
        refetchInterval: canOffer ? 30_000 : false,
    });

    const offer = offerQ.data?.offer || null;
    const recipients = offerQ.data?.recipients || [];
    const isOpen = offer?.status === 'open';

    const withdrawMutation = useMutation({
        mutationFn: () => api.post(`/offers/${offer.id}/withdraw`),
        onSuccess: () => {
            toast.success('Offer withdrawn');
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id, 'offer'] });
        },
        onError: (err) => toast.error(err?.message || 'Could not withdraw offer'),
    });

    // Accepted offer — small provenance line once allocated.
       if (offer?.status === 'accepted' && booking.allocated_chauffeur_id) {
        const who = [offer.accepted_chauffeur_first_name, offer.accepted_chauffeur_last_name].filter(Boolean).join(' ');
        return (
            <div className="text-xs text-ink-subtle">
                Allocated via job offer{who ? ` — ${who}` : ''}
            </div>
        );
    }

    if (!canOffer) return null;

    const opened = recipients.filter((r) => r.first_opened_at).length;
    const total = (offer?.pay_lines || []).reduce((s, l) => s + (Number(l.amount_ex_vat) || 0), 0);

    return (
        <>
            {isOpen ? (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm">
                    <div className="font-medium text-blue-900">
                        Offer out · sent to {recipients.length}
                    </div>
                    <div className="text-xs text-blue-800 mt-0.5">
                        {opened} opened · {money(total)} ex VAT · {(offer.allowed_classes || []).map((c) => c.toUpperCase()).join(', ')}
                    </div>
                    {recipients.length > 0 && (
                        <div className="text-xs text-blue-800/80 mt-1.5 leading-relaxed">
                            {recipients.map((r) => {
                                const name = r.is_operator && r.fleet_partner_company_name
                                    ? r.fleet_partner_company_name
                                    : [r.first_name, r.last_name].filter(Boolean).join(' ');
                                return `${name}${r.first_opened_at ? ' ✓' : ''}`;
                            }).join(', ')}
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setShowModal(true)}
                            className="text-xs text-blue-900 underline decoration-dotted"
                        >
                            Edit &amp; resend
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (window.confirm('Withdraw this offer? Links already sent will show "withdrawn".')) {
                                    withdrawMutation.mutate();
                                }
                            }}
                            disabled={withdrawMutation.isPending}
                            className="text-xs text-red-700 underline decoration-dotted"
                        >
                            Withdraw
                        </button>
                    </div>
                </div>
            ) : (
                <Button variant="secondary" className="w-full" onClick={() => setShowModal(true)}>
                    <Send className="h-4 w-4" />
                    Send job offer
                </Button>
            )}

            {showModal && (
                <SendOfferModal
                    booking={booking}
                    existingOffer={isOpen ? offer : null}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
}

// -----------------------------------------------------------------------------
// SendOfferModal
// -----------------------------------------------------------------------------
export function SendOfferModal({ booking, existingOffer, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const bookedClass = CLASS_OPTIONS.some((c) => c.key === booking.vehicle_class)
        ? booking.vehicle_class
        : 'business';

    const [classes, setClasses] = useState(
        existingOffer?.allowed_classes?.length ? existingOffer.allowed_classes : [bookedClass]
    );
    const [selected, setSelected] = useState(() => new Set());
    const [payLines, setPayLines] = useState(() =>
        existingOffer?.pay_lines?.length
            ? existingOffer.pay_lines.map((l) => ({
                label: l.label,
                amount: String(l.amount_ex_vat ?? ''),
                line_type: l.line_type || 'fare',
            }))
            : [{ label: 'Fare', amount: '', line_type: 'fare' }]
    );
    const [note, setNote] = useState(existingOffer?.note || '');
    const [sendEmail, setSendEmail] = useState(true);
    const [sendSms, setSendSms] = useState(false);

       const classKey = [...classes].sort().join(',');

    const [areas, setAreas] = useState([]);
    const areaKey = [...areas].sort().join(',');
    const areaOptionsQ = useQuery({
        queryKey: ['service-areas'],
        queryFn: () => api.get('/offers/meta/service-areas'),
    });
    const areaOptions = areaOptionsQ.data?.areas || [];

    function toggleArea(a) {
        setAreas((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
    }

    const candidatesQ = useQuery({
        queryKey: ['offer-candidates', booking.id, classKey, areaKey],
        queryFn: () => api.get(`/offers/booking/${booking.id}/candidates?classes=${classKey}&areas=${encodeURIComponent(areaKey)}`),
        enabled: classes.length > 0,
    });
    const candidates = candidatesQ.data?.candidates || [];

    // Whenever the candidate list changes, default-select everyone eligible.
    useEffect(() => {
        if (!candidatesQ.data) return;
        setSelected(new Set(candidates.filter((c) => c.ok).map((c) => c.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidatesQ.data]);

    function toggleClass(key) {
        setClasses((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));
    }

    function toggleRecipient(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function updateLine(i, patch) {
        setPayLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
    }

    const total = payLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
    const linesValid = payLines.length > 0 && payLines.every((l) =>
        l.label.trim() && l.amount !== '' && Number.isFinite(parseFloat(l.amount)) && parseFloat(l.amount) >= 0
    );
    const eligibleCount = candidates.filter((c) => c.ok).length;
    const canSend = classes.length > 0 && selected.size > 0 && linesValid && (sendEmail || sendSms);

    const sendMutation = useMutation({
                mutationFn: () => api.post(`/offers/booking/${booking.id}`, {
            classes,
            areas,
            driver_ids: [...selected],
            pay_lines: payLines.map((l) => ({
                label: l.label.trim(),
                amount_ex_vat: parseFloat(l.amount),
                line_type: l.line_type,
            })),
            note: note.trim(),
            send_email: sendEmail,
            send_sms: sendSms,
        }),
        onSuccess: (data) => {
            const sentCount = (data?.sent || []).length - (data?.failed || []).length;
            toast.success(`Offer sent to ${sentCount}`);
            if (data?.failed?.length) {
                toast.error(`Could not reach: ${data.failed.map((f) => f.name).join(', ')}`);
            }
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id, 'offer'] });
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            onClose();
        },
        onError: (err) => toast.error(err?.message || 'Could not send offer'),
    });

    return (
        <Modal
            open
            onClose={sendMutation.isPending ? undefined : onClose}
            title={existingOffer ? 'Edit & resend job offer' : 'Send job offer'}
            description={`${booking.ride_ref} — first to accept is allocated automatically.`}
            size="lg"
        >
            <div className="space-y-6 mt-2">
                {existingOffer && (
                    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2 flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>Sending replaces the current offer. Links already sent will show "withdrawn".</span>
                    </div>
                )}

                {/* 1. Classes */}
                <section>
                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2">
                        Offer to vehicle classes
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {CLASS_OPTIONS.map((c) => {
                            const on = classes.includes(c.key);
                            return (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => toggleClass(c.key)}
                                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                        on
                                            ? 'bg-ink text-paper border-ink'
                                            : 'bg-paper text-ink-subtle border-paper-border hover:text-ink'
                                    }`}
                                >
                                    {c.label}
                                    {c.key === bookedClass && <span className="ml-1 text-[10px] opacity-75">(booked)</span>}
                                </button>
                            );
                        })}
                    </div>
                </section>

                                {/* 1b. Areas */}
                {areaOptions.length > 0 && (
                    <section>
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2">
                            Service areas {areas.length === 0 && <span className="normal-case tracking-normal font-normal">· none ticked = all areas</span>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {areaOptions.map((a) => {
                                const on = areas.includes(a);
                                return (
                                    <button
                                        key={a}
                                        type="button"
                                        onClick={() => toggleArea(a)}
                                        className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                            on
                                                ? 'bg-ink text-paper border-ink'
                                                : 'bg-paper text-ink-subtle border-paper-border hover:text-ink'
                                        }`}
                                    >
                                        {a}
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* 2. Recipients */}
                <section>
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                            Recipients · {selected.size} selected
                        </div>
                        {eligibleCount > 0 && (
                            <div className="flex gap-3 text-xs">
                                <button type="button" className="text-ink-info hover:underline"
                                    onClick={() => setSelected(new Set(candidates.filter((c) => c.ok).map((c) => c.id)))}>
                                    All
                                </button>
                                <button type="button" className="text-ink-info hover:underline"
                                    onClick={() => setSelected(new Set())}>
                                    None
                                </button>
                            </div>
                        )}
                    </div>
                    {classes.length === 0 ? (
                        <p className="text-sm text-ink-subtle italic">Pick at least one class.</p>
                    ) : candidatesQ.isLoading ? (
                        <p className="text-sm text-ink-subtle italic">Loading…</p>
                    ) : candidates.length === 0 ? (
                        <p className="text-sm text-ink-subtle italic">No chauffeurs or operators with an active vehicle in these classes.</p>
                    ) : (
                        <div className="border border-paper-border-soft rounded-md divide-y divide-paper-border-soft max-h-72 overflow-y-auto">
                            {candidates.map((c) => (
                                <label
                                    key={c.id}
                                    className={`flex items-start gap-3 px-3 py-2.5 ${c.ok ? 'cursor-pointer hover:bg-canvas/40' : 'opacity-50 cursor-not-allowed'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(c.id)}
                                        onChange={() => toggleRecipient(c.id)}
                                        disabled={!c.ok}
                                        className="mt-0.5"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-ink flex items-center gap-2 flex-wrap">
                                            <span className="font-medium">
                                                {c.is_operator && c.company ? c.company : c.name}
                                            </span>
                                            {c.is_operator && (
                                                <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                    Operator
                                                </span>
                                            )}
                                            {c.call_sign && <span className="text-xs font-mono text-ink-subtle">{c.call_sign}</span>}
                                            {c.email && <Mail className="h-3 w-3 text-ink-subtle" />}
                                            {c.phone && <Phone className="h-3 w-3 text-ink-subtle" />}
                                        </div>
                                        {c.is_operator && c.company && (
                                            <div className="text-xs text-ink-subtle">{c.name}</div>
                                        )}
                                        <div className="text-xs text-ink-subtle mt-0.5">
                                        {c.vehicles.map((v) => `${v.label} (${v.vehicle_class.toUpperCase()})`).join(' · ')}
                                        </div>
                                        <div className="text-xs text-ink-subtle mt-0.5">
                                            {(c.service_areas || []).length > 0 ? (
                                                c.service_areas.join(', ')
                                            ) : (
                                                <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                                                    No area set
                                                </span>
                                            )}
                                        </div>
                                        {!c.ok && c.issues.length > 0 && (
                                            <div className="text-xs text-red-700 mt-0.5">{c.issues.join(' · ')}</div>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </section>

                {/* 3. Pay */}
                <section>
                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2">
                        Pay to chauffeur (ex VAT)
                    </div>
                    <div className="space-y-2">
                        {payLines.map((l, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                                <input
                                    type="text"
                                    value={l.label}
                                    onChange={(e) => updateLine(i, { label: e.target.value })}
                                    placeholder={l.line_type === 'expense' ? 'e.g. Car park' : 'Description'}
                                    className="flex-1 min-w-0 text-sm border border-paper-border rounded px-2 py-1.5 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                                />
                                <button
                                    type="button"
                                    onClick={() => updateLine(i, { line_type: l.line_type === 'fare' ? 'expense' : 'fare' })}
                                    className={`flex-shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded px-2 py-1.5 border ${
                                        l.line_type === 'expense'
                                            ? 'bg-amber-100 border-amber-300 text-amber-800'
                                            : 'bg-canvas border-paper-border text-ink-subtle'
                                    }`}
                                    title="Click to switch Fare / Expense"
                                >
                                    {l.line_type === 'expense' ? 'Expense' : 'Fare'}
                                </button>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={l.amount}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) updateLine(i, { amount: v });
                                    }}
                                    placeholder="0.00"
                                    className="w-24 text-right text-sm border border-paper-border rounded px-2 py-1.5 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                                />
                                <button
                                    type="button"
                                    onClick={() => setPayLines((prev) => prev.filter((_, idx) => idx !== i))}
                                    disabled={payLines.length === 1}
                                    className="text-ink-subtle hover:text-red-700 disabled:opacity-30"
                                    title="Remove line"
                                >
                                    <XCircle className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-1">
                            <button
                                type="button"
                                onClick={() => setPayLines((prev) => [...prev, { label: '', amount: '', line_type: 'expense' }])}
                                className="text-xs text-ink-info hover:underline inline-flex items-center gap-1"
                            >
                                <Plus className="h-3 w-3" />
                                Add line (e.g. car park)
                            </button>
                            <div className="text-sm text-ink">
                                Total <span className="font-semibold">{money(total)}</span>
                                <span className="text-xs text-ink-subtle"> ex VAT</span>
                            </div>
                        </div>
                        <p className="text-xs text-ink-subtle">
                            VAT-registered recipients see "+ VAT"; 20% is added to their billing lines on accept.
                        </p>
                    </div>
                </section>

                {/* 4. Note */}
                <section>
                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2">
                        Note to chauffeurs <span className="normal-case tracking-normal">(optional)</span>
                    </div>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        placeholder="e.g. Meet & greet with name board, suit & tie"
                    />
                </section>

                {/* 5. Channels */}
                <section className="flex flex-wrap gap-5">
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                        Email
                    </label>
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                        <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
                        SMS <span className="text-xs text-ink-subtle">(charged per recipient)</span>
                    </label>
                </section>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => sendMutation.mutate()}
                    disabled={!canSend}
                    loading={sendMutation.isPending}
                >
                    <Send className="h-4 w-4" />
                    Send offer{selected.size > 0 ? ` to ${selected.size}` : ''}
                </Button>
            </div>
        </Modal>
    );
}
